import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
    CookieConsent,
    getStoredConsent,
    saveConsent,
} from '../utils/cookieConsent';
import CookieConsentBanner from '../components/cookies/CookieConsentBanner';
import CookiePreferencesModal from '../components/cookies/CookiePreferencesModal';

interface CookieConsentContextValue {
    /** Current decision, or null if the visitor hasn't decided yet. */
    consent: CookieConsent | null;
    /** True once the visitor has made (and we have stored) a decision. */
    decided: boolean;
    /** Open the granular preferences modal — usable any time, e.g. from the policy page. */
    openPreferences: () => void;
    acceptAll: () => void;
    rejectAll: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

/**
 * Owns the cookie-consent decision and renders both the consent banner (until
 * a decision is made) and the always-available preferences modal. It is the
 * single React writer of consent; `saveConsent` also notifies the non-React
 * SDK gates (Amplitude, Sentry replay) so they react in the same tick.
 *
 * Must live inside the Router (the banner links to /privacy).
 */
export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [consent, setConsent] = useState<CookieConsent | null>(() => getStoredConsent());
    const [prefsOpen, setPrefsOpen] = useState(false);

    const commit = useCallback((choice: { analytics: boolean }) => {
        setConsent(saveConsent(choice));
    }, []);

    const acceptAll = useCallback(() => commit({ analytics: true }), [commit]);
    const rejectAll = useCallback(() => commit({ analytics: false }), [commit]);
    const openPreferences = useCallback(() => setPrefsOpen(true), []);
    const closePreferences = useCallback(() => setPrefsOpen(false), []);

    const decided = consent !== null;

    return (
        <CookieConsentContext.Provider
            value={{ consent, decided, openPreferences, acceptAll, rejectAll }}
        >
            {children}

            {!decided && (
                <CookieConsentBanner
                    onAcceptAll={acceptAll}
                    onRejectAll={rejectAll}
                    onManage={openPreferences}
                />
            )}

            <CookiePreferencesModal
                isOpen={prefsOpen}
                onClose={closePreferences}
                initial={consent}
                onSave={(choice) => {
                    commit(choice);
                    closePreferences();
                }}
                onAcceptAll={() => {
                    acceptAll();
                    closePreferences();
                }}
            />
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = (): CookieConsentContextValue => {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within a CookieConsentProvider');
    }
    return context;
};
